import { useCallback, useMemo, useState } from "react";
import { ExerciseData, ExerciseNavigationHookReturn } from "../types";

export const useExerciseNavigation = (): ExerciseNavigationHookReturn => {
  const [exerciseHistory, setExerciseHistory] = useState<ExerciseData[]>([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
  const [isNavigating, setIsNavigating] = useState(false);

  const canGoToPrevious = currentHistoryIndex > 0;
  const canGoToNext = currentHistoryIndex < exerciseHistory.length - 1;

  const goToPreviousExercise = useCallback(() => {
    if (canGoToPrevious) {
      setIsNavigating(true);
      setCurrentHistoryIndex((prev) => prev - 1);
      setTimeout(() => setIsNavigating(false), 100);
    }
  }, [canGoToPrevious]);

  const goToNextExercise = useCallback(
    (onGenerateNew: () => void) => {
      if (canGoToNext) {
        // Navigate to next exercise in history
        setIsNavigating(true);
        setCurrentHistoryIndex((prev) => prev + 1);
        setTimeout(() => setIsNavigating(false), 100);
      } else {
        // Generate new exercise
        onGenerateNew();
      }
    },
    [canGoToNext]
  );

  const addToHistory = useCallback(
    (exercise: ExerciseData) => {
      if (!isNavigating) {
        setExerciseHistory((prev) => {
          const newHistory = [...prev, exercise];
          // Keep history to reasonable size (max 50 exercises)
          return newHistory.slice(-50);
        });
        setCurrentHistoryIndex((prev) => prev + 1);
      }
    },
    [isNavigating]
  );

  const resetNavigation = useCallback(() => {
    setExerciseHistory([]);
    setCurrentHistoryIndex(-1);
    setIsNavigating(false);
  }, []);

  return useMemo(
    () => ({
      exerciseHistory,
      currentHistoryIndex,
      isNavigating,
      canGoToPrevious,
      canGoToNext,
      goToPreviousExercise,
      goToNextExercise,
      addToHistory,
      resetNavigation,
    }),
    [
      exerciseHistory,
      currentHistoryIndex,
      isNavigating,
      canGoToPrevious,
      canGoToNext,
      goToPreviousExercise,
      goToNextExercise,
      addToHistory,
      resetNavigation,
    ]
  );
};
